import React, { useState, useRef } from 'react'
import { Button, Input, TinyMCE, FileDropzone } from 'components'
import Flex from 'components/priority/Flex/Flex'
import { Tooltip } from '@/components/global/Tooltip/Tooltip'
import { PersonalImageCard } from 'components/dashboard/CaseDetails'
import { ToggleSwitch } from 'components/global/ToggleSwitch/ToggleSwitch'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import {
  stylizedVersesAtom,
  themeOverrideAtom,
  editorProductIdAtom,
} from 'atoms'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'
import { useMayMakeVerseGlobal } from '@/hooks/utility/useMayMakeVerseGlobal'
import { IMAGE_MIME_WILDCARD } from '@messenger/consts'
import { useSetToast } from 'hooks'
import { useTranslation } from 'react-i18next'
import {
  createCustomVerse,
  getCustomVerses,
  removeStylizedVerseImage,
  uploadStylizedVerseImage,
} from '@/api/customVerses'
import { getThemeOverrideProductId } from 'utils/editor/themeOverrideProductId'

const CustomVerseEditor = ({ setCreatingVerse, stylizedVerse }) => {
  const setStylizedVerses = useSetRecoilState(stylizedVersesAtom)
  const themeOverride = useRecoilValue(themeOverrideAtom)
  const product = useRecoilValue(editorProductIdAtom)
  const handleError = useErrorHandler('CustomVerseEditorMenu')
  const TinyMCERef = useRef(null)
  const setToast = useSetToast()
  const isAdmin = useIsAdmin()
  // Hidden, not disabled, for a caller the server refuses a global verse.
  const mayMakeVerseGlobal = useMayMakeVerseGlobal()
  const { t } = useTranslation()
  const [reference, setReference] = useState('')
  const [verseImage, setVerseImage] = useState(null)
  const [isGlobalVerse, setIsGlobalVerse] = useState(false)
  const [isThemeVerse, setIsThemeVerse] = useState(false)
  const [imagesLoading, setImagesLoading] = useState(false)
  const [isCreating, setIsCreating] = useState(false)

  const { theme_id: defaultThemeId } = themeOverride || {}
  const defaultProductId = getThemeOverrideProductId(themeOverride)

  const uploadVerseImage = async (file) => {
    setImagesLoading(true)
    try {
      const fd = new FormData()
      fd.append('file', file[0], 'blob')
      const resp = await uploadStylizedVerseImage(fd)
      setImagesLoading(false)
      if (resp.data) setVerseImage(resp.data)
      return resp.data
    } catch (error) {
      handleError(
        error?.response?.data?.message || error.message,
        error,
        null,
        'uploadStylizedVerseImage',
      )
      throw error
    }
  }

  const removeVerseImage = async () => {
    try {
      const resp = await removeStylizedVerseImage(stylizedVerse?.id, {
        filename: verseImage,
      })
      if (resp) setVerseImage(null)
    } catch (error) {
      handleError(
        error?.response?.data?.message || error.message,
        error,
        null,
        'removeVerseImage',
      )
    }
  }

  const createVerse = async () => {
    if (isCreating) return
    setIsCreating(true)
    const canNotCreate =
      isAdmin && !isGlobalVerse && !isThemeVerse && stylizedVerse
    if (canNotCreate) {
      setToast({
        text: t('editor.menus.assets.stylizedVerses.createStylizedVerseError'),
      })
      setIsCreating(false)
      return
    }
    try {
      let base_product_id = null
      if (isThemeVerse && !isGlobalVerse) {
        base_product_id = defaultProductId
      }
      const content = TinyMCERef?.current?.getContent?.()
      const newVerse = {
        html: content || '',
        html_for_render: stylizedVerse ? '' : content,
        reference: reference,
        isGlobal: isGlobalVerse,
        type: stylizedVerse ? 'IMAGE' : 'HTML',
        image_url: verseImage,
        base_product_id,
        theme_id: isGlobalVerse ? null : isThemeVerse ? defaultThemeId : null,
        order_product_id: !isAdmin && stylizedVerse ? product : null,
      }
      await createCustomVerse(newVerse)
      const stylizedVersesData = await getCustomVerses(
        defaultThemeId,
        defaultProductId,
        product,
      )
      setStylizedVerses(stylizedVersesData.data)
      setCreatingVerse(false)
      setIsCreating(false)
    } catch (error) {
      setIsCreating(false)
      handleError(error?.response?.data?.message || error.message, error)
    }
  }

  const MemoizedTinyMCE = React.memo(TinyMCE)

  const canUploadVerseImage = stylizedVerse && !verseImage

  return (
    <Flex column full>
      {!stylizedVerse && (
        <Flex
          border="1px solid"
          borderColor="gray4"
          radius="2px"
          minHeight="200px"
          margin="0 0 12px 0"
          full
        >
          <MemoizedTinyMCE
            placeholder={t(
              'editor.menus.assets.stylizedVerses.typeTextPlaceholder',
            )}
            value={TinyMCERef?.current?.getContent?.()}
            setRef={(ref) => (TinyMCERef.current = ref)}
          />
        </Flex>
      )}

      {canUploadVerseImage && (
        <Flex full column>
          <FileDropzone
            height="200px"
            margin="0 0 24px 0"
            text={t(
              'editor.menus.assets.stylizedVerses.dragAndDropPlaceholder',
            )}
            fileTypes={[IMAGE_MIME_WILDCARD]}
            allowMultiple={false}
            onDrop={(file) => uploadVerseImage(file)}
            isLoading={imagesLoading}
          />
        </Flex>
      )}

      {verseImage && (
        <Flex full column width="100%">
          <PersonalImageCard
            image={verseImage}
            index={0}
            makeDefault={false}
            deleteImage={() => removeVerseImage()}
            margin="0 0 24px 0"
            showDefault={true}
          />
        </Flex>
      )}
      <Input
        margin="0 0 12px 0"
        label={t('editor.menus.assets.stylizedVerses.referenceLabel')}
        value={[reference, setReference]}
      />

      {isAdmin && (
        <Flex
          justify="flex-start"
          align="flex-start"
          margin="16px 0"
          gap="8px 0"
          column
        >
          {mayMakeVerseGlobal && (
            <Tooltip
              text={t('editor.menus.assets.stylizedVerses.makeVerseGlobalText')}
              margin="0 12px 0 0"
            >
              <ToggleSwitch
                label={t(
                  'editor.menus.assets.stylizedVerses.isVerseGlobalText',
                )}
                labelRight
                value={[isGlobalVerse, setIsGlobalVerse]}
                disabled={!isAdmin}
              />
            </Tooltip>
          )}
          {stylizedVerse && !isGlobalVerse && (
            <Tooltip
              text={t(
                'editor.menus.assets.stylizedVerses.linkVerseToProductText',
              )}
              margin="0 12px 0 0"
            >
              <ToggleSwitch
                label={t(
                  'editor.menus.assets.stylizedVerses.isLinkedVerseText',
                )}
                labelRight
                value={[isThemeVerse, setIsThemeVerse]}
                disabled={!isAdmin}
              />
            </Tooltip>
          )}
        </Flex>
      )}

      <Flex>
        <Button onClick={() => setCreatingVerse(false)} margin="0 12px 0 0">
          {t('editor.menus.assets.stylizedVerses.cancelButton')}
        </Button>
        <Button primary onClick={createVerse}>
          {t('editor.menus.assets.stylizedVerses.saveButton')}
        </Button>
      </Flex>
    </Flex>
  )
}

export const CustomVerseEditorMenu = ({ setCreatingVerse, stylizedVerse }) => (
  <Flex full padding="24px">
    <CustomVerseEditor
      setCreatingVerse={setCreatingVerse}
      stylizedVerse={stylizedVerse}
    />
  </Flex>
)

export default CustomVerseEditorMenu
